
import React, { useState, useEffect } from 'react';
import { Workflow, RefreshCw, ExternalLink, Activity, Globe, ShieldCheck, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { N8N_EDITOR_URL, N8N_CONFIG } from '../constants';
import { checkServerConnection, HealthCheckResult } from '../services/n8nService';

const WorkflowMonitor: React.FC = () => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [health, setHealth] = useState<HealthCheckResult | null>(null);

    const checkAllWorkflows = async () => {
        setIsRefreshing(true);
        // Wir prüfen NUR den Kanzlei Bot
        const res = await checkServerConnection(N8N_CONFIG.KANZLEI_BOT_WEBHOOK);
        setHealth(res);
        setIsRefreshing(false);
    };

    useEffect(() => {
        checkAllWorkflows();
        const interval = setInterval(checkAllWorkflows, 5000); 
        return () => clearInterval(interval);
    }, []);

    // DIAGNOSTIC BADGE COMPONENT
    const DiagnosticBadge = ({ data }: { data: HealthCheckResult }) => {
        if (data.status === 'ONLINE') {
            return (
                <div className="bg-emerald-950/50 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg flex items-start space-x-3 shadow-[0_0_15px_rgba(16,185,129,0.1)] animate-fade-in">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-bold text-sm">SYSTEM OPERATIONAL</div>
                        <div className="text-xs text-emerald-200/70 mt-1">
                            Webhook aktiv & erreichbar. Latenz: {data.latency}ms. <br/>
                            <span className="opacity-70 font-mono text-[10px]">(HTTP Code {data.code}: {data.message})</span>
                        </div>
                    </div>
                </div>
            );
        }

        if (data.status === 'MISSING_WORKFLOW') {
             return (
                 <div className="bg-amber-950/50 border border-amber-500/50 text-amber-400 px-4 py-3 rounded-lg flex items-start space-x-3 animate-pulse">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-bold text-sm">WORKFLOW MISSING (404)</div>
                        <div className="text-xs text-amber-200/70 mt-1">
                            Der Webhook <code>{N8N_CONFIG.KANZLEI_BOT_WEBHOOK}</code> antwortet nicht.<br/>
                            Ist der Workflow im Editor auf "Active"?
                        </div>
                    </div>
                </div>
            );
        }

        // OFFLINE
        return (
             <div className="bg-red-950/50 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-start space-x-3">
                <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                    <div className="font-bold text-sm">SYSTEM OFFLINE</div>
                    <div className="text-xs text-red-200/70 mt-1">
                        Keine Verbindung zum Server. Docker Container down?
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-white">Orbit Core Engine</h2>
                    <p className="text-slate-400 mt-1">Status der Kanzlei-Automatisierung.</p>
                </div>
                <div className="flex space-x-3">
                    <a href={N8N_EDITOR_URL} target="_blank" className="bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-bold border border-slate-700 flex items-center hover:bg-slate-700 transition-all">
                        <ExternalLink className="w-4 h-4 mr-2" /> Editor öffnen
                    </a>
                    <button 
                        onClick={checkAllWorkflows} 
                        className={`p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* LIVE KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex flex-col justify-between hover:border-blue-500/30 transition-all">
                     <div className="flex justify-between items-start mb-4">
                         <div className="p-3 bg-blue-900/20 rounded-xl">
                             <Globe className="w-6 h-6 text-blue-400" />
                         </div>
                         <div className="text-xs font-mono text-slate-500">Latency</div>
                     </div>
                     <div>
                         <div className="text-2xl font-bold text-white">{health ? `${health.latency}ms` : '---'}</div>
                         <div className="text-xs text-slate-500">Internal Docker Network</div>
                     </div>
                </div>

                <div className={`bg-slate-900 border p-6 rounded-2xl flex flex-col justify-between transition-all ${health?.status !== 'ONLINE' ? 'border-red-500/50' : 'border-slate-800'}`}>
                     <div className="flex justify-between items-start mb-4">
                         <div className={`p-3 rounded-xl ${health?.status !== 'ONLINE' ? 'bg-red-900/20' : 'bg-emerald-900/20'}`}>
                             {health?.status !== 'ONLINE' ? <AlertTriangle className="w-6 h-6 text-red-400" /> : <ShieldCheck className="w-6 h-6 text-emerald-400" />}
                         </div>
                         <div className="text-xs font-mono text-slate-500">Health</div>
                     </div>
                     <div>
                         <div className={`text-2xl font-bold ${health?.status === 'ONLINE' ? 'text-white' : 'text-red-400'}`}>
                            {health?.status || 'Waiting...'}
                         </div>
                         <div className="text-xs text-slate-500">
                             Target: {N8N_CONFIG.KANZLEI_BOT_WEBHOOK}
                         </div>
                     </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex flex-col justify-between hover:border-purple-500/30 transition-all">
                     <div className="flex justify-between items-start mb-4">
                         <div className="p-3 bg-purple-900/20 rounded-xl">
                             <Activity className="w-6 h-6 text-purple-400" />
                         </div>
                         <div className="text-xs font-mono text-slate-500">System</div>
                     </div>
                     <div>
                         <div className="text-2xl font-bold text-white">v6.1.2 Stable</div>
                         <div className="text-xs text-slate-500">Kanzlei Core Active</div>
                     </div>
                </div>
            </div>

            {/* REAL WORKFLOWS LIST */}
            <div className="space-y-6">
                
                {health && (
                    <div className="bg-slate-950 border border-slate-800 p-6 rounded-xl">
                        <div className="flex items-center mb-4">
                            <Workflow className="w-5 h-5 mr-3 text-slate-400" />
                            <h4 className="font-bold text-white">Active Pipeline: {N8N_CONFIG.KANZLEI_BOT_WEBHOOK}</h4>
                        </div>
                        
                        <DiagnosticBadge data={health} />
                        
                        <div className="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-500">
                            <strong>System Note:</strong> Ein "500 Error" beim Health-Check ist normal und wird als "Online" gewertet, da der Workflow ohne Input-Daten korrekt fehlschlägt.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkflowMonitor;
